import { Injectable, inject } from "@angular/core";
import { Observable, of } from "rxjs";
import { Mottagningsplatstyper } from "~app/common/models/constants";
import { MottagningsplatsListRequestDTO } from "~app/common/models/lagerplats/MottagningsplatsListRequestDTO";
import { MottagningsplatsListResponseDTO } from "~app/common/models/lagerplats/MottagningsplatsListResponseDTO";
import { ITypeAheadResult } from "~app/common/models/type-ahead-result.model";
import { ApiClientService } from "~app/common/services/api-client.service";
import {
    IDeleteMottagningsplats,
    ILagerplats,
    ILand,
    IMottagningsplats,
    IMottagningsplatsgrunddata,
    ISDCMottagningsplats,
    ISearchMottagningsplatsQueryResult,
    Mottagningsplats
} from "./mottagningsplats.models";

@Injectable({
    providedIn: "root"
})
export class MottagningsplatsService {
    private readonly apiClient = inject(ApiClientService);
    private lander: ILand[];

    search(request: MottagningsplatsListRequestDTO): Observable<MottagningsplatsListResponseDTO> {
        return this.apiClient.post<
            MottagningsplatsListRequestDTO,
            MottagningsplatsListResponseDTO
        >("Kundorder/SearchForMottagningsplats", request, true);
    }

    searchMottagningsplatserWithLagerplatser(params: any) {
        return this.apiClient.get<ISearchMottagningsplatsQueryResult[]>(`mottagningsplats/Search`, false, params);
    }

    getMottagningsplatsById(id: number): Observable<IMottagningsplats> {
        return this.apiClient.get<IMottagningsplats>(`Kundorder/GetMottagningsplatsById/${id}`, true);
    }

    fetchMottagningsplatsBasdata(): void {
        this.apiClient.get<IMottagningsplatsgrunddata>("Kundorder/GetMottagningsplatsBasData", true)
            .subscribe(x => {
                this.lander = x.lander;
            });
    }

    searchLander(input: string): Observable<ILand[]> {
        if (!this.lander) {
            return of([]);
        }

        const inputLower = input.toLowerCase();
        const matches = [];
        const exactMatch = this.lander.find(x => x.kod.toLowerCase() === inputLower);
        if (exactMatch) {
            matches.push(exactMatch);
        }

        matches.push(...this.lander.filter(x => x.namn.toLowerCase().indexOf(inputLower) > -1 && x.kod !== exactMatch?.kod));
        return of(matches);
    }

    searchForAnsvarigLogistikerTypeahead(input: string): Observable<ITypeAheadResult[]> {
        return this.apiClient.post<any, ITypeAheadResult[]>("typeahead/anvandare", { query: input }, false, null, ApiClientService.JsonHeader);
    }

    searchForLeverantorTerminalByNamnHkInternnrTypeahead(input: string): Observable<ITypeAheadResult[]> {
        return this.apiClient.post<any, ITypeAheadResult[]>(`Leverantorsorder/SearchForLeverantorTerminalByNamnHkInternnrTypeahead`, `"${input}"`, false, null,
            ApiClientService.JsonHeader);
    }

    searchForSveaskogKundNamnHkInternnummerTypeAhead(input: string): Observable<ITypeAheadResult[]> {
        return this.apiClient.post<any, ITypeAheadResult[]>(`Kund/SearchForSveaskogKundNamnHkInternnummerTypeAhead`, `"${input}"`, true, null, ApiClientService.JsonHeader);
    }

    searchForSDCMottagningsplatsTypeAhead(input: string): Observable<ITypeAheadResult[]> {
        return this.apiClient.post<any, ITypeAheadResult[]>(`Kundorder/SearchForSDCMottagningsplatsTypeAhead`, `"${input}"`, true, null, ApiClientService.JsonHeader);
    }

    getSDCMottagningsplatsByKod(kod: string): Observable<ISDCMottagningsplats> {
        return this.apiClient.get<ISDCMottagningsplats>(`Kundorder/GetSDCMottagningsplatsByKod?kod=${kod}`, true);
    }

    saveMottagningsplats(mottagningsplats: IMottagningsplats): Observable<IMottagningsplats> {
        return this.apiClient.post<IMottagningsplats, IMottagningsplats>(`Kundorder/SaveMottagningsplats`, mottagningsplats, true, null, null);
    }

    getNewMottagningsplats(): Observable<IMottagningsplats> {
        return this.apiClient.get<IMottagningsplats>(`Kundorder/GetNewMottagningsplats`, true);
    }

    deleteMottagningsplatsById(deleteMottagningsplats: IDeleteMottagningsplats): Observable<void> {
        return this.apiClient.post<IDeleteMottagningsplats, void>(`Kundorder/DeleteMottagningsplatsById`, deleteMottagningsplats, true, null, null);
    }

    createEmptyMottagningsplats(isSvenskMottagningsplats: boolean): Mottagningsplats {
        const mottagningsplats = new Mottagningsplats();
        mottagningsplats.mottagningsplatsnummer = undefined;
        mottagningsplats.namn = undefined;
        mottagningsplats.egenTerminal = false;
        mottagningsplats.ansvarigLogistiker = undefined;
        mottagningsplats.terminalCKLeverantor = undefined;
        mottagningsplats.terminalKund = undefined;
        mottagningsplats.easting = undefined;
        mottagningsplats.northing = undefined;
        mottagningsplats.site = undefined;
        mottagningsplats.mottagningsplatstyp = Mottagningsplatstyper.Mottagningsplats;
        mottagningsplats.beskrivning = "";
        mottagningsplats.land = undefined;
        mottagningsplats.lagerplatser = [];
        mottagningsplats.beraknaNarmasteIndustri = false;

        if (!isSvenskMottagningsplats) {
            const lagerplats: ILagerplats = {
                avlamningsplats: "0",
                oppettider: "",
                matmetoder: [],
                koordinatN: 0,
                koordinatE: 0,
                finnsIViol3: false,
                aktiv: true
            };
            lagerplats.mottagningsplatsKod = undefined;
            lagerplats.oppettider = "";
            lagerplats.matmetoder = [];
            lagerplats.koordinatN = 0;
            lagerplats.koordinatE = 0;
            lagerplats.finnsIViol3 = false;
            mottagningsplats.lagerplatser.push(lagerplats);
        }

        return mottagningsplats;
    }
}
